/**
 * Headless Embla initializer for slideshow-component[data-embla].
 * Provides basic Embla carousel behavior without navigation UI.
 * When a carousel-navigation-component is also present, it will
 * find the existing instance via target._embla and reuse it.
 *
 * Also provides the slideshow.js-compatible API (.pause(), .select(),
 * .resume(), .current, .next(), .previous()) so that theme-editor.js,
 * collection-links.js, media-gallery.js, etc. can interact with the
 * slideshow-component element without the old slideshow.js class.
 */

import { SlideshowSelectEvent } from '@theme/events';

function setup(/** @type {HTMLElement} */ target) {
  if (/** @type {any} */ (target)._embla) return;

  const container = /** @type {HTMLElement | null} */ (target.querySelector('slideshow-container'));
  const slidesContainer = /** @type {HTMLElement | null} */ (container?.querySelector('slideshow-slides'));
  if (!container || !slidesContainer) return;

  const EmblaCarousel = /** @type {any} */ (window).EmblaCarousel;
  if (!EmblaCarousel) return;

  // Disable native slideshow scroll-snap behavior
  target.setAttribute('disabled', 'true');
  const component = /** @type {any} */ (target);
  if (component.scroller) component.scroller = null;

  // Viewport setup
  container.style.overflowX = 'clip';
  container.style.overflowY = 'visible';
  if (container.firstElementChild !== slidesContainer) {
    container.prepend(slidesContainer);
  }

  // Page-margin alignment (skip for full-width)
  if (target.dataset.embla !== 'full-width') {
    container.style.marginLeft = 'var(--page-margin, 40px)';
    container.style.width = 'calc(100% - var(--page-margin, 40px))';
  }

  // Resource-list carousels use CSS padding approach for gaps (managed by
  // carousel-navigation-embla.js). Skip inline gap/margin overrides for them.
  const isResourceList = target.classList.contains('resource-list__carousel');

  // Override native scroll-snap on the slides container
  slidesContainer.style.overflow = 'visible';
  slidesContainer.style.scrollSnapType = 'none';
  slidesContainer.style.scrollBehavior = 'auto';
  if (!isResourceList) {
    slidesContainer.style.gap = '0';
    slidesContainer.style.padding = '0';
  }
  slidesContainer.style.cursor = 'grab';
  slidesContainer.style.willChange = 'transform';
  slidesContainer.style.backfaceVisibility = 'hidden';

  const slideEls = /** @type {HTMLElement[]} */ (Array.from(slidesContainer.children));

  if (!isResourceList) {
    // Read gap from CSS variables
    const cs = getComputedStyle(target);
    const gapRaw =
      cs.getPropertyValue('--resource-list-column-gap-desktop').trim() ||
      cs.getPropertyValue('--carousel-gap').trim() ||
      cs.getPropertyValue('--slideshow-gap').trim();
    const gapPx = parseFloat(gapRaw) || 0;

    // Apply margin-based gap (Embla uses margins, not CSS gap)
    slideEls.forEach((slide) => {
      slide.style.marginRight = `${gapPx}px`;
    });
  }

  // Accessibility plugin
  const plugins = [];
  try {
    const A11y = /** @type {any} */ (window).EmblaCarouselAccessibility;
    if (typeof A11y === 'function') {
      plugins.push(A11y({ active: true }));
    }
  } catch (_) {
    // Plugin not available — continue without it
  }

  const embla = EmblaCarousel(
    container,
    {
      loop: true,
      slidesToScroll: 'auto',
      align: 'start',
      duration: 25,
    },
    plugins,
  );

  // Store on element for carousel-navigation-component to discover
  component._embla = embla;
  component._emblaViewport = container;
  component._emblaSlides = slideEls;

  // --- Slideshow.js-compatible API ---
  // Mirrors the old Slideshow class so theme-editor.js, collection-links.js,
  // media-gallery.js, etc. work without slideshow.js.

  /** Internal current index */
  let _currentIndex = 0;

  /** Tracks whether the current goTo call is programmatic (no user event) */
  let _programmatic = false;

  /**
   * The `current` property setter syncs dots, thumbnails, and counter —
   * the same side effects the old Slideshow class `set current()` performed.
   */
  Object.defineProperty(component, 'current', {
    get() {
      return _currentIndex;
    },
    set(value) {
      _currentIndex = value;

      // Update counter text
      const counter = target.querySelector('[ref="current"]');
      if (counter) counter.textContent = `${value + 1}`;

      // Update thumbnails
      target.querySelectorAll('[ref="thumbnails[]"]').forEach((el, i) => {
        el.setAttribute('aria-selected', `${i === value}`);
      });

      // Update dots
      target.querySelectorAll('[ref="dots[]"]').forEach((el, i) => {
        el.setAttribute('aria-selected', `${i === value}`);
      });
    },
    configurable: true,
  });

  /** Pauses autoplay. */
  component.pause = function () {
    component._paused = true;
    if (typeof component._stopAutoplay === 'function') component._stopAutoplay();
  };

  /** Resumes autoplay if not paused. */
  component.resume = function () {
    if (component._paused) return;
    if (typeof component._startAutoplay === 'function') component._startAutoplay();
  };

  /**
   * Selects a slide by index.
   * @param {number|string|{id:string}} input
   * @param {Event} [event]
   * @param {{animate?: boolean}} [options]
   */
  component.select = function (input, event, options) {
    if (!component._embla) return;

    let index;
    if (typeof input === 'number') {
      index = input;
    } else if (typeof input === 'string') {
      index = parseInt(input, 10);
    } else if (input && typeof input === 'object' && 'id' in input) {
      // Find slide by slide-id attribute
      const slide = slideEls.find((s) => s.getAttribute('slide-id') == input.id);
      index = slide ? slideEls.indexOf(slide) : 0;
    }

    if (index == null || isNaN(index)) return;

    // Clamp to valid range
    const snapCount = component._embla.snapList().length;
    if (snapCount <= 0) return;
    index = ((index % snapCount) + snapCount) % snapCount;

    // Mark as programmatic when no user event is passed (matches old Slideshow behavior)
    _programmatic = event == null;

    // Second arg = jump (skip animation) when animate is false
    const jump = options?.animate === false;
    component._embla.goTo(index, jump);
  };

  /** Advances to the next slide. */
  component.next = function () {
    if (component._embla) component._embla.goToNext();
  };

  /** Goes to the previous slide. */
  component.previous = function () {
    if (component._embla) component._embla.goToPrev();
  };

  /** Returns visible (non-hidden) slides. */
  Object.defineProperty(component, 'slides', {
    get() {
      return slideEls.filter((s) => !s.hasAttribute('hidden'));
    },
    configurable: true,
  });

  // --- Event dispatch ---
  // Dispatch SlideshowSelectEvent on Embla select so that listeners
  // (e.g. collection-links.js) receive slide-change notifications.

  embla.on('select', () => {
    const index = embla.selectedSnap();
    const previousIndex = _currentIndex;
    const userInitiated = !_programmatic;
    _programmatic = false;

    component.current = index;

    const slide = slideEls[index];
    if (!slide) return;

    target.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      }),
    );
  });
}

function initAll() {
  document.querySelectorAll('slideshow-component[data-embla]').forEach((el) => {
    if (el instanceof HTMLElement) setup(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
